import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './Order.css';

const PLANS = [
  {
    id: 'monthly',
    label: 'Mensuel',
    price: '50 € / mois',
    amount: 50,
    features: ['Google Drive', 'Mises à jour auto', 'Support 7j/7'],
    popular: false,
  },
  {
    id: 'annual',
    label: 'Annuel',
    price: '480 € / an',
    sub: 'soit 40 €/mois — économisez 20 %',
    amount: 480,
    features: ['Google Drive', 'Mises à jour auto', 'Support 7j/7 prioritaire', 'Garantie 30j remboursé'],
    popular: true,
  },
];

export default function Order() {
  const { client } = useAuth();
  const [plan, setPlan] = useState('annual');
  const [domain, setDomain] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const selectedPlan = PLANS.find(p => p.id === plan);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/modules/orders', { plan_type: plan, domain, notes });
      setSuccess(res.data.order);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la commande.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="order-page">
        <Navbar />
        <div className="container order-content">
          <div className="order-success">
            <div className="success-icon">🎉</div>
            <h1>Commande enregistrée !</h1>
            <p>Votre commande <strong>#{success.id}</strong> a bien été prise en compte.</p>

            <div className="cheque-instructions">
              <h2>📬 Instructions de paiement par chèque</h2>
              <div className="instruction-steps">
                <div className="instruction-row">
                  <span className="instr-label">Chèque à l'ordre de</span>
                  <strong>Technonet</strong>
                </div>
                <div className="instruction-row">
                  <span className="instr-label">Montant</span>
                  <strong>{selectedPlan?.amount} €</strong>
                </div>
                <div className="instruction-row">
                  <span className="instr-label">Au dos du chèque</span>
                  <code>{success.id} — {client?.email}</code>
                </div>
                <div className="instruction-row">
                  <span className="instr-label">Adresse d'envoi</span>
                  <span>Contactez-nous à <a href="mailto:contact@technonet.fr">contact@technonet.fr</a> pour obtenir l'adresse postale.</span>
                </div>
              </div>
              <div className="alert alert-info" style={{ marginTop: 24 }}>
                ⏱️ Délai de traitement : <strong>3 à 5 jours ouvrés</strong> après réception du chèque.<br />
                Votre clé de licence sera envoyée à <strong>{client?.email}</strong>.
              </div>
            </div>

            <div className="success-actions">
              <Link to="/dashboard" className="btn btn-primary">Voir mon espace client</Link>
              <Link to="/" className="btn btn-outline">Retour à l'accueil</Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="order-page">
      <Navbar />

      <div className="order-header">
        <div className="container">
          <Link to="/dashboard" className="back-link">← Mon espace</Link>
          <h1>Commander EvBackupManager Premium</h1>
          <p>Déverrouillez Google Drive et le support 7j/7</p>
        </div>
      </div>

      <div className="container order-content">
        <form onSubmit={handleSubmit} className="order-form">

          {/* ── Choix du plan ── */}
          <div className="order-section">
            <h2>1. Choisissez votre formule</h2>
            <div className="plan-selector">
              {PLANS.map(p => (
                <label key={p.id} className={`plan-option ${plan === p.id ? 'selected' : ''} ${p.popular ? 'popular' : ''}`}>
                  <input type="radio" name="plan" value={p.id} checked={plan === p.id} onChange={() => setPlan(p.id)} />
                  <div className="plan-option-body">
                    {p.popular && <span className="popular-tag">⭐ Recommandé</span>}
                    <div className="plan-name">{p.label}</div>
                    <div className="plan-price">{p.price}</div>
                    {p.sub && <div className="plan-sub">{p.sub}</div>}
                    <ul className="plan-feats">
                      {p.features.map((f, i) => <li key={i}>✓ {f}</li>)}
                    </ul>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* ── Domaine ── */}
          <div className="order-section">
            <h2>2. Votre boutique PrestaShop</h2>
            <div className="form-group">
              <label>Nom de domaine *</label>
              <input
                className="form-control"
                type="text"
                placeholder="maboutique.fr"
                value={domain}
                onChange={e => setDomain(e.target.value)}
                required
              />
              <p className="form-hint">La licence sera liée à ce domaine. Indiquez uniquement le domaine, sans http:// ni www.</p>
            </div>
            <div className="form-group">
              <label>Notes <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optionnel)</span></label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Informations complémentaires..."
                value={notes}
                onChange={e => setNotes(e.target.value)}
              />
            </div>
          </div>

          {/* ── Résumé + paiement ── */}
          <div className="order-section order-summary">
            <h2>3. Résumé &amp; paiement</h2>
            <div className="summary-box">
              <div className="summary-row">
                <span>Produit</span>
                <strong>EvBackupManager Premium</strong>
              </div>
              <div className="summary-row">
                <span>Formule</span>
                <strong>{selectedPlan?.label}</strong>
              </div>
              <div className="summary-row">
                <span>Domaine</span>
                <strong>{domain || '—'}</strong>
              </div>
              <div className="summary-row summary-total">
                <span>Total</span>
                <strong>{selectedPlan?.amount} €</strong>
              </div>
            </div>

            <div className="payment-info">
              <h4>💳 Mode de paiement : Chèque</h4>
              <p>Après validation de votre commande, vous recevrez les instructions pour envoyer votre chèque à Technonet. La licence sera créée dès encaissement (3-5 jours ouvrés).</p>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <button type="submit" className="btn btn-accent btn-lg btn-block" disabled={loading}>
              {loading
                ? <><span className="spinner" /> Enregistrement…</>
                : `✅ Valider ma commande — ${selectedPlan?.amount} €`
              }
            </button>

            <p className="order-note">En validant, vous acceptez de régler par chèque dans les 10 jours. La licence sera activée à réception.</p>
          </div>

        </form>
      </div>

      <Footer />
    </div>
  );
}
